import {
  EditableReservoirParameter,
  ReservoirParameter,
  ReservoirType,
} from '~/shared/models/system/ReservoirModel';

// Fields in ui are displayed in the order defined below.
export class ReservoirParameterConfigModel {
  private static allParameterers: Record<ReservoirType, ReservoirParameter[]> =
    {
      conventionalRoof: ['type', 'area'],
      blueRoof: [
        'capacityUsedPercentage',
        'capacityUsedVolume',
        'currentWaterLevel',
        'type',
        'crateType',
        'area',
        'factorHollow',
        'minWaterHeight',
        'maxWaterHeight',
        'targetWaterHeight',
        'targetWaterHeightGrowingMonths',
      ],
      greenRoof: [
        'capacityUsedPercentage',
        'capacityUsedVolume',
        'currentWaterLevel',
        'type',
        'crateType',
        'greenType',
        'areaGreen',
        'area',
        'factorHollow',
        'minWaterHeight',
        'maxWaterHeight',
        'targetWaterHeight',
        'targetWaterHeightGrowingMonths',
        'externalIrrigation',
      ],
      tank: [
        'capacityUsedPercentage',
        'capacityUsedVolume',
        'currentWaterLevel',
        'type',
        'area',
        'maxWaterHeight',
        'volume',
        'factorHollow',
        'captureArea',
        'dryProtection',
        'minWaterCapacityPercentage',
        'targetWaterCapacityPercentage',
        'targetWaterCapacityPercentageGrowingMonths',
        'externalUsage',
      ],
      infiltrationTank: [
        'capacityUsedPercentage',
        'capacityUsedVolume',
        'currentWaterLevel',
        'type',
        'area',
        'maxWaterHeight',
        'volume',
        'factorHollow',
        'minWaterCapacityPercentage',
        'targetWaterCapacityPercentage',
        'targetWaterCapacityPercentageGrowingMonths',
        'drainageSpeed',
      ],
      pond: [
        'capacityUsedPercentage',
        'capacityUsedVolume',
        'currentWaterLevel',
        'type',
        'pondNapOffset',
        'area',
        'minWaterHeight',
        'maxWaterHeight',
        'targetWaterHeight',
        'targetWaterHeightGrowingMonths',
      ],
      wadi: [
        'capacityUsedPercentage',
        'capacityUsedVolume',
        'currentWaterLevel',
        'type',
        'drainageSpeed',
        'wadiMaxVolume',
        'area',
        'minWaterHeight',
        'maxWaterHeight',
        'targetWaterHeight',
        'targetWaterHeightGrowingMonths',
      ],
    };

  private static advancedParametersExcludedFromLimitedView: Set<ReservoirParameter> =
    new Set([
      'crateType',
      'factorHollow',
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
      'externalIrrigation',
      'captureArea',
      'dryProtection',
      'minWaterCapacityPercentage',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
      'volumeCalcStrategy',
      'externalUsage',
      'drainageSpeed',
      'pondNapOffset',
      'wadiMaxVolume',
    ]);

  private static advancedParametersExcludedFromAdviceSystemState: Set<ReservoirParameter> =
    new Set([
      'capacityUsedPercentage',
      'capacityUsedVolume',
      'currentWaterLevel',
      'factorHollow',
      'minWaterHeight',
      'minWaterCapacityPercentage',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
      'dryProtection',
    ]);

  private static limitedPermissionParameters: Record<
    ReservoirType,
    ReservoirParameter[]
  > = Object.fromEntries(
    Object.entries(ReservoirParameterConfigModel.allParameterers).map(
      ([type, params]) => [
        type,
        params.filter(
          (param) =>
            !ReservoirParameterConfigModel.advancedParametersExcludedFromLimitedView.has(
              param,
            ),
        ),
      ],
    ),
  ) as Record<ReservoirType, ReservoirParameter[]>;

  static parametersForReport: Record<ReservoirType, ReservoirParameter[]> = {
    conventionalRoof: ['type', 'area'],
    blueRoof: ['type', 'area', 'crateType'],
    greenRoof: ['type', 'area', 'areaGreen', 'crateType'],
    tank: ['type', 'crateType'],
    infiltrationTank: ['type', 'crateType'],
    pond: ['type', 'area'],
    wadi: ['type', 'area'],
  };

  private static editableFields: Record<
    ReservoirType,
    EditableReservoirParameter[]
  > = {
    conventionalRoof: ['title', 'type', 'area'],
    blueRoof: [
      'title',
      'type',
      'crateType',
      'area',
      'factorHollow',
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
    ],
    greenRoof: [
      'title',
      'type',
      'crateType',
      'greenType',
      'areaGreen',
      'area',
      'factorHollow',
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
      'externalIrrigation',
    ],
    tank: [
      'title',
      'type',
      'volumeCalcStrategy',
      'area',
      'maxWaterHeight',
      'volume',
      'factorHollow',
      'captureArea',
      'dryProtection',
      'minWaterCapacityPercentage',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
      'externalUsage',
    ],
    infiltrationTank: [
      'title',
      'type',
      'volumeCalcStrategy',
      'area',
      'maxWaterHeight',
      'volume',
      'factorHollow',
      'minWaterCapacityPercentage',
      'targetWaterCapacityPercentage',
      'targetWaterCapacityPercentageGrowingMonths',
      'drainageSpeed',
    ],
    pond: [
      'title',
      'type',
      'pondNapOffset',
      'area',
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
    ],
    wadi: [
      'title',
      'type',
      'drainageSpeed',
      'wadiMaxVolume',
      'area',
      'minWaterHeight',
      'maxWaterHeight',
      'targetWaterHeight',
      'targetWaterHeightGrowingMonths',
    ],
  };

  private static editableFieldsInAdviceSystemState: Record<
    ReservoirType,
    EditableReservoirParameter[]
  > = Object.fromEntries(
    Object.entries(ReservoirParameterConfigModel.editableFields).map(
      ([type, fields]) => [
        type,
        fields.filter(
          (field) =>
            !ReservoirParameterConfigModel.advancedParametersExcludedFromAdviceSystemState.has(
              field,
            ),
        ),
      ],
    ),
  ) as Record<ReservoirType, EditableReservoirParameter[]>;

  static getEditableFields({
    reservoirType,
    inAdviceSystemState = false,
  }: {
    reservoirType: ReservoirType;
    inAdviceSystemState?: boolean;
  }): EditableReservoirParameter[] {
    if (inAdviceSystemState) {
      return ReservoirParameterConfigModel.editableFieldsInAdviceSystemState[
        reservoirType
      ];
    }
    return ReservoirParameterConfigModel.editableFields[reservoirType];
  }

  static getDisplayableParameters({
    reservoirType,
    includeAdvancedParameters = false,
    inAdviceState = false,
  }: {
    reservoirType: ReservoirType;
    includeAdvancedParameters?: boolean;
    inAdviceState?: boolean;
  }): ReservoirParameter[] {
    const parameters = includeAdvancedParameters
      ? ReservoirParameterConfigModel.allParameterers[reservoirType]
      : ReservoirParameterConfigModel.limitedPermissionParameters[
          reservoirType
        ];

    if (inAdviceState) {
      return parameters.filter(
        (param) =>
          !ReservoirParameterConfigModel.advancedParametersExcludedFromAdviceSystemState.has(
            param,
          ),
      );
    }

    return parameters;
  }
}
